import React from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { TouchApp } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import {
  physicalAssistanceBased,
  PROMPTING_LEVEL_LABELS,
  PHYSICAL_ASSISTANCE_TOOLTIPS,
} from '../../../constants/GoalsConstants';

const PhysicalAssistanceFields = ({
  dataPoint,
  index,
  handleFieldChange,
  goalType,
  allGoalData,
}) => {
  // Helper function to render all tooltips in a formatted list
  const renderAllTooltips = () => {
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {PHYSICAL_ASSISTANCE_TOOLTIPS.map((item) => (
          <li key={item.key}>
            <strong>{item.label}</strong>: {item.tooltip}
          </li>
        ))}
      </ul>
    );
  };

  // Helper function to get tooltip content based on physical assistance level
  const getPhysicalAssistanceTooltip = (physicalAssistanceLevel) => {
    // If no level is selected, show all tooltips
    if (!physicalAssistanceLevel) {
      return renderAllTooltips();
    }

    // Find the specific tooltip for the selected level
    const tooltipItem = PHYSICAL_ASSISTANCE_TOOLTIPS.find(
      (item) => item.key === physicalAssistanceLevel
    );
    return tooltipItem ? tooltipItem.tooltip : renderAllTooltips();
  };

  const getDataPointPhysicalAssistanceLevel = () => {
    // Check if this is a subgoal
    if (
      allGoalData &&
      dataPoint.currentGoal &&
      dataPoint.currentGoal !== 'main'
    ) {
      const subgoals =
        allGoalData?.HasSubgoal?.Subgoals ||
        allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
        [];

      // Try multiple matching strategies to locate the sub-goal definition
      let targetSubgoal =
        subgoals.find((sg) => sg.SubgoalNumber === dataPoint.currentGoal) ||
        subgoals.find(
          (sg) => parseInt(sg.SubgoalNumber) === parseInt(dataPoint.currentGoal)
        ) ||
        subgoals.find(
          (sg) => String(sg.SubgoalNumber) === String(dataPoint.currentGoal)
        );

      if (!targetSubgoal) {
        // Strategy: index based (if currentGoal is "1", pick subgoals[0])
        const goalIndex = parseInt(dataPoint.currentGoal) - 1;
        if (goalIndex >= 0 && goalIndex < subgoals.length) {
          targetSubgoal = subgoals[goalIndex];
        }
      }

      if (targetSubgoal?.PhysicalAssistanceLevel) {
        return targetSubgoal.PhysicalAssistanceLevel;
      }
    }

    // --- Main goal physical assistance level -----------------------------------------
    const mainPhysicalAssistanceLevel =
      allGoalData?.DataCollection?.measurementType?.PhysicalAssistanceLevel ||
      allGoalData?.dataCollection?.DataCollection?.measurementType
        ?.PhysicalAssistanceLevel ||
      '';

    return mainPhysicalAssistanceLevel || '';
  };

  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  // Physical Assistance Section
  const renderPhysicalAssistance = () => {
    if (goalType !== 'Physical Assistance') return null;

    // Use the physical assistance level for the specific subgoal this data entry belongs to
    const rawLevel = getDataPointPhysicalAssistanceLevel();
    const physicalAssistanceTooltip = getPhysicalAssistanceTooltip(rawLevel);

    // Get the current selected level from dataPoint or use the goal's default
    const selectedLevel = dataPoint.physicalAssistanceLevel || rawLevel || '';

    return (
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'transparent',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TouchApp sx={{ color: '#6b7280', mr: 1, fontSize: 18 }} />
          <Typography
            variant='body1'
            sx={{
              color: '#1f2937',
              fontWeight: 400,
              fontSize: '1rem',
            }}
          >
            Physical Assistance Level
          </Typography>
          <Tooltip title={physicalAssistanceTooltip}>
            <InfoIcon
              sx={{
                fontSize: 'small',
                ml: 0.5,
                verticalAlign: 'middle',
                cursor: 'help',
                color: '#6b7280',
              }}
            />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography
            variant='body1'
            sx={{
              color: '#374151',
              fontWeight: 500,
            }}
          >
            Physical Assistance Level:
          </Typography>
          <FormControl sx={{ minWidth: 300 }}>
            <Select
              value={selectedLevel}
              onChange={(e) =>
                handleFieldChange(index, 'physicalAssistanceLevel', e.target.value)
              }
              displayEmpty
              margin='dense'
              className='input-height'
              sx={{ backgroundColor: 'var(--clr-mindly-white)' }}
            >
              <MenuItem value=''>Select physical assistance level</MenuItem>
              {physicalAssistanceBased.map((value) => (
                <MenuItem key={value} value={value}>
                  {PROMPTING_LEVEL_LABELS[value] || value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>
    );
  };

  return <>{renderPhysicalAssistance()}</>;
};

export default PhysicalAssistanceFields;

